import * as Sentry from '@sentry/react';
import { getGlobalUserId } from '.';

/**
 * Logs an error to Sentry with proper user context and additional tags
 * @param {Error} error - The error object to log
 * @param {Object} context - Additional context tags to attach to the error
 */
export const logError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    // Ensure user ID is always set, even if global context was missed
    const userId = getGlobalUserId();
    console.log('id', userId);
    if (userId) {
      scope.setUser({ id: userId });
    }

    // Set context tags
    Object.keys(context).forEach((key) => {
      scope.setTag(key, context[key]);
    });

    // Add breadcrumb for better context
    Sentry.addBreadcrumb({
      message: error.message || 'Error occurred',
      level: 'error',
      data: context,
    });

    // Capture the exception
    Sentry.captureException(error);
  });
};

/**
 * Logs a message to Sentry with proper user context
 * @param {string} message - The message to log
 * @param {string} level - The severity level (debug, info, warning, error, fatal)
 * @param {Object} context - Additional context tags
 */
export const logMessage = (message, level = 'info', context = {}) => {
  Sentry.withScope((scope) => {
    // Ensure user ID is always set
    const userId = getGlobalUserId();
    if (userId) {
      scope.setUser({ id: userId });
    }

    // Set context tags
    Object.keys(context).forEach((key) => {
      scope.setTag(key, context[key]);
    });

    // Add breadcrumb
    Sentry.addBreadcrumb({
      message,
      level,
      data: context,
    });

    // Capture the message
    Sentry.captureMessage(message, level);
  });
};

/**
 * Clears the Sentry user context (used on logout)
 */
export const clearSentryUserContext = () => {
  Sentry.configureScope((scope) => {
    scope.clear();
  });
};

/**
 * Sets custom context data for the current Sentry scope
 * @param {string} key - The context key
 * @param {any} data - The context data
 */
export const setSentryContext = (key, data) => {
  Sentry.setContext(key, data);
};

/**
 * Adds a breadcrumb for better error tracking
 * @param {Object} breadcrumb - The breadcrumb data
 */
